import { useSyncExternalStore } from 'react';
import { accountProfileIdFor, accountTagFor, DEFAULT_SETTINGS } from '@autoday/core';
import { apiAt, configureApi } from '../api.ts';
import { defaultApiBase, originOf } from '../config.ts';
import { isNative, randomId } from '../platform.ts';
import {
  broadcastProfileChange,
  GUEST_ID,
  openRegistry,
  readRegistry,
  updateRegistry,
  type LegacyProbe,
  type ProfileMeta,
  type Registry,
} from '../profile/registry.ts';
import { pendingTransfer, initialAccountState, transferGuestToAccount, type TransferCounts, type TransferResult } from '../profile/transfer.ts';
import type { AppStore } from '../store/appStore.ts';
import { emptyDevice, type StorageAdapter } from '../store/storage.ts';
import { clearLiveSession, getLiveSession, saveReturnTo, setLiveSession, setNotice } from '../ui/uiSession.ts';
import { getSession, isNetworkError, signIn, signOut, signUp, type AuthUser } from './authClient.ts';
import * as tokens from './secureToken.ts';
import { tr } from '../i18n/i18n.ts';

/**
 * Ett tillstånd i taget – inga parallella booleska flaggor.
 *  - checking:  kontoprofil, sessionen kontrolleras mot servern
 *  - guest:     lokal profil ("Endast på den här enheten")
 *  - signed-in: sessionen är VERIFIERAD av servern (get-session)
 *  - offline:   tidigare verifierad, servern nås inte nu – ett nätverksfel är inte en utgången session
 *  - expired:   servern säger att sessionen upphört – synk pausad, lokala ändringar bevaras i profilen
 * Väntande serverutloggningar (utloggning offline) redovisas separat i `pendingRevocations`.
 */
export type AuthState =
  | { status: 'checking'; profile: ProfileMeta }
  | { status: 'guest'; locked: ProfileMeta | null }
  | { status: 'signed-in'; profile: ProfileMeta; user: AuthUser }
  /** Tidigare verifierad, men servern kunde inte bekräfta nu: nätverket saknas, eller servern svarade med fel/429. */
  | { status: 'offline'; profile: ProfileMeta; reason: 'network' | 'server' }
  | { status: 'expired'; profile: ProfileMeta };

export interface AuthSnapshot {
  state: AuthState;
  pendingRevocations: number;
  /** Profilbyte pågår – sidan laddas om. */
  switching: boolean;
}

export interface LoginInput {
  signal?: AbortSignal;
  mode: 'login' | 'register';
  email: string;
  password: string;
  name?: string;
  rememberMe: boolean;
  /** Uttryckligen vald server (Egen server / avancerat). undefined = standard. */
  server?: string;
}

export interface LoginOutcome {
  target: ProfileMeta;
  user: AuthUser;
  token: string | null;
  base: string;
  /** Gästdata som KAN läggas till i kontot – kräver aktivt val. null = inget att föra över. */
  transferOffer: TransferCounts | null;
}

export type LoginCommitStep = 'session' | 'local' | 'transfer' | 'activate' | 'reload';

export type StorageFactory = (dbName: string) => StorageAdapter;

const nowIso = () => new Date().toISOString();

export class AuthStore {
  private snap: AuthSnapshot;
  private listeners = new Set<() => void>();
  private store: AppStore | null = null;
  private busy: Promise<unknown> | null = null;
  private revoking: Promise<void> | null = null;
  private verifyRetry: ReturnType<typeof setTimeout> | null = null;
  /** Körs före utloggning (pausa synk, koppla bort notiser). */
  readonly beforeSignOut: Array<(p: ProfileMeta) => Promise<void>> = [];
  /** Anropas när en serverutloggning bekräftats efter offline-utloggning. */
  onRevocationResolved: ((kind: 'revoked' | 'nothing') => void) | null = null;

  private constructor(
    private registry: Registry,
    /** Profilen vars data denna sidladdning visar. Ändras bara via omladdning. */
    readonly profile: ProfileMeta,
    private makeStorage: StorageFactory,
    lockedProfile: ProfileMeta | null,
  ) {
    const state: AuthState = profile.kind === 'account' ? { status: 'checking', profile } : { status: 'guest', locked: lockedProfile };
    this.snap = { state, pendingRevocations: registry.pendingRevocations.length, switching: false };
  }

  /**
   * Väljer vilken profil sidan ska visa. En icke-ihågkommen webbsession som inte lever i denna flik
   * och som inte kan kontrolleras (offline) visas INTE – gästvyn visas tills servern kan fråga.
   */
  static async open(probe: () => Promise<LegacyProbe | null>, makeStorage: StorageFactory): Promise<AuthStore> {
    const reg = await openRegistry(probe);
    const active = reg.profiles[reg.activeProfileId] ?? reg.profiles[GUEST_ID]!;
    if (active.kind === 'account' && active.rememberMe === false && getLiveSession() !== active.id && !isNative() && !navigator.onLine) {
      return new AuthStore(reg, reg.profiles[GUEST_ID]!, makeStorage, active);
    }
    return new AuthStore(reg, active, makeStorage, null);
  }

  get snapshot(): AuthSnapshot {
    return this.snap;
  }
  get state(): AuthState {
    return this.snap.state;
  }
  subscribe = (l: () => void) => {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  };
  private emit(p: Partial<AuthSnapshot>) {
    this.snap = { ...this.snap, ...p };
    for (const l of this.listeners) l();
  }
  private setState(state: AuthState) {
    this.emit({ state });
  }

  attachStore(store: AppStore) {
    this.store = store;
  }

  /** API-bas för en profil. '' = samma ursprung. null = ingen server konfigurerad. */
  baseFor(p: ProfileMeta = this.profile): string | null {
    if (p.kind === 'account') return p.server ?? defaultApiBase();
    return defaultApiBase();
  }

  /** Kopplar API-lagret till den aktiva profilen: rätt server, rätt token, rätt förväntat konto. */
  configureApi() {
    const p = this.profile;
    configureApi({
      baseUrl: this.baseFor(p) ?? '',
      token: () => (p.kind === 'account' ? tokens.getToken(p.id) : Promise.resolve(null)),
      expectedUser: () => (p.kind === 'account' ? (p.userId ?? null) : null),
    });
  }

  async start(): Promise<void> {
    window.addEventListener('online', () => {
      void this.processPendingRevocations();
      if (this.snap.state.status === 'offline') void this.verify();
      if (this.snap.state.status === 'guest' && this.snap.state.locked) this.reload();
    });
    if (navigator.onLine) void this.processPendingRevocations();
    if (this.profile.kind === 'account') await this.verify();
  }

  // ------------------------------------------------------------------ session

  /** Kontrollerar sessionen enligt better-auths kontrakt (get-session). */
  async verify(): Promise<void> {
    const p = this.profile;
    if (p.kind !== 'account') return;
    const base = this.baseFor(p) ?? '';
    const live = getLiveSession() === p.id;
    // Native: en icke-ihågkommen session avslutas vid kall start (sessionStorage överlever bara omladdning).
    if (p.rememberMe === false && !live && isNative()) {
      await this.endNotRemembered();
      return;
    }
    try {
      const user = await getSession(base, await tokens.getToken(p.id));
      if (!user) {
        if (p.rememberMe === false) await this.endNotRemembered();
        else this.setState({ status: 'expired', profile: p });
        return;
      }
      if (p.userId && user.id !== p.userId) {
        // Sessionen tillhör ett annat konto (t.ex. inloggning i en annan flik). Visa inte detta kontos
        // synk som aktiv – profilbytet i den andra fliken laddar om denna.
        this.setState({ status: 'expired', profile: p });
        return;
      }
      if (!p.userId) {
        // Profil från versionen före profilerna: koppla användar-ID först när e-posten stämmer.
        if (user.email.toLowerCase() !== (p.email ?? '').toLowerCase()) {
          this.setState({ status: 'expired', profile: p });
          return;
        }
        p.userId = user.id;
        p.accountTag = accountTagFor(user.id);
      }
      p.name = user.name;
      p.email = user.email;
      p.lastVerifiedAt = nowIso();
      await updateRegistry((r) => ({ ...r, profiles: { ...r.profiles, [p.id]: { ...r.profiles[p.id]!, userId: p.userId, accountTag: p.accountTag, name: user.name, email: user.email, lastVerifiedAt: p.lastVerifiedAt } } }));
      setLiveSession(p.id);
      this.setState({ status: 'signed-in', profile: p, user });
    } catch (e) {
      // Nätverksfel eller serverfel är inte en utgången session (get-session svarar null – inte ett fel –
      // när sessionen verkligen upphört). Lokal data raderas aldrig på grund av ett timeout.
      const reason = isNetworkError(e) ? 'network' : 'server';
      this.setState({ status: 'offline', profile: p, reason });
      // Nätverk: nytt försök vid "online". Serverfel/429: nytt försök efter Retry-After (30 s–5 min).
      if (reason === 'server' && !this.verifyRetry) {
        const after = (e as { retryAfter?: number | null }).retryAfter;
        const ms = Math.min(300, Math.max(30, typeof after === 'number' && after > 0 ? after : 30)) * 1000;
        this.verifyRetry = setTimeout(() => {
          this.verifyRetry = null;
          if (this.snap.state.status === 'offline') void this.verify();
        }, ms);
      }
    }
  }

  /** Synken fick 401: sessionen har verkligen upphört. */
  markExpired() {
    const s = this.snap.state;
    if (s.status === 'signed-in' || s.status === 'offline' || s.status === 'checking') this.setState({ status: 'expired', profile: this.profile });
  }

  /** Servern sa ACCOUNT_MISMATCH: cookien tillhör ett annat konto. Följ registret. */
  async handleAccountMismatch() {
    const reg = await readRegistry();
    if (reg && reg.activeProfileId !== this.profile.id) this.reload();
    else this.markExpired();
  }

  // ------------------------------------------------------------------ inloggning

  /**
   * Steg 1: logga in/registrera hos servern och VERIFIERA sessionen. Byter inte profil ännu – om
   * gästdata finns måste användaren först välja vad som ska hända med den.
   */
  async login(input: LoginInput): Promise<LoginOutcome> {
    if (this.busy) throw new Error(tr('En inloggning pågår redan.', 'A sign-in is already in progress.'));
    const run = (async (): Promise<LoginOutcome> => {
      // Webbens tidigare cookie måste återkallas före ett nytt inloggningsförsök.
      // Native använder separata bärartoken per profil. Där kan återkallelserna
      // fortsätta i bakgrunden utan att blockera en ny inloggning på telefonen.
      if (navigator.onLine && !isNative()) await this.processPendingRevocations().catch(() => undefined);
      input.signal?.throwIfAborted();
      const base = input.server !== undefined ? input.server : defaultApiBase();
      if (base === null) throw new Error(tr('Ingen AutoDay-server är inställd för den här appen. Ange din server under Egen server / avancerat.', 'No AutoDay server is set for this app. Enter your server under Own server / advanced.'));
      const res =
        input.mode === 'register'
          ? await signUp(base, { name: input.name ?? '', email: input.email, password: input.password, rememberMe: input.rememberMe }, input.signal)
          : await signIn(base, { email: input.email, password: input.password, rememberMe: input.rememberMe }, input.signal);
      // Visa aldrig inloggat förrän servern bekräftat sessionen.
      input.signal?.throwIfAborted();
      const verified = await getSession(base, res.token, input.signal);
      if (!verified || verified.id !== res.user.id) throw new Error(tr('Inloggningen kunde inte bekräftas av servern. Försök igen.', 'The server could not confirm the sign-in. Try again.'));
      const serverOrigin = originOf(base);
      const id = accountProfileIdFor(serverOrigin, verified.id);
      const reg = (await readRegistry()) ?? this.registry;
      input.signal?.throwIfAborted();
      let existing: ProfileMeta | undefined = reg.profiles[id];
      if (!existing) {
        existing = Object.values(reg.profiles).find(
          (p) => p.legacy && !p.userId && (p.serverOrigin ?? '') === serverOrigin && (p.email ?? '').toLowerCase() === verified.email.toLowerCase(),
        );
      }
      const target: ProfileMeta = {
        ...(existing ?? { id, kind: 'account' as const, dbName: `autoday-${id}`, createdAt: nowIso() }),
        server: base,
        serverOrigin,
        userId: verified.id,
        email: verified.email,
        name: verified.name,
        accountTag: accountTagFor(verified.id),
        rememberMe: input.rememberMe,
        lastVerifiedAt: nowIso(),
        signedOut: false,
      };
      let transferOffer: TransferCounts | null = null;
      if (this.profile.kind === 'guest' && this.store) {
        const { counts } = pendingTransfer(this.store.state, this.store.device, target.id);
        transferOffer = counts.total > 0 ? counts : null;
      }
      return { target, user: verified, token: res.token, base, transferOffer };
    })();
    this.busy = run;
    try {
      return await run;
    } finally {
      this.busy = null;
    }
  }

  /**
   * Steg 2: byt till kontots profil. 'transfer' lägger till gästdata i kontot (aktivt val);
   * 'keep' lämnar den endast lokalt. Laddar om sidan – inga sena svar eller cachar från förra profilen
   * kan visas efteråt.
   */
  async finishLogin(o: LoginOutcome, choice: 'transfer' | 'keep', onProgress?: (step: LoginCommitStep) => void): Promise<TransferResult | null> {
    if (this.busy) throw new Error(tr('Ett kontobyte pågår redan.', 'An account switch is already in progress.'));
    const run = (async () => {
      const t = o.target;
      onProgress?.('session');
      if (o.token) await tokens.setToken(t.id, o.token);
      // At re-authentication this profile is already loaded in this WebView.
      // Opening and querying its native SQLite connection again is unnecessary.
      const alreadyOpen = this.store !== null && this.profile.kind === 'account' && this.profile.id === t.id;
      let storage: StorageAdapter | null = null;
      if (!alreadyOpen) {
        onProgress?.('local');
        storage = this.makeStorage(t.dbName);
        if (!(await storage.load())) await storage.persist(null, initialAccountState(this.store?.state.settings ?? DEFAULT_SETTINGS));
        if (!(await storage.loadDevice())) await storage.saveDevice({ ...emptyDevice(randomId('dev_')), firstRunDone: true });
      }
      let result: TransferResult | null = null;
      if (choice === 'transfer' && this.store && this.profile.kind === 'guest' && storage) {
        onProgress?.('transfer');
        result = await transferGuestToAccount(this.store, storage, t.id);
      }
      onProgress?.('activate');
      await updateRegistry((r) => ({ ...r, activeProfileId: t.id, profiles: { ...r.profiles, [t.id]: t } }));
      setLiveSession(t.id);
      saveReturnTo();
      const name = t.name?.trim() || t.email || tr('ditt konto', 'your account');
      setNotice(
        result
          ? tr(
              `Inloggad som ${name}. ${result.added} poster lades till i kontot${result.skipped ? `; ${result.skipped} fanns redan där med annat innehåll och lämnades orörda` : ''}${result.plans ? `; ${result.plans} förberedelseplan${result.plans === 1 ? '' : 'er'} läggs till med sina pass när kontot synkas` : ''}. Originalen finns kvar lokalt.`,
              `Signed in as ${name}. ${result.added} items were added to the account${result.skipped ? `; ${result.skipped} were already there with different content and were left untouched` : ''}${result.plans ? `; ${result.plans} preparation plan${result.plans === 1 ? '' : 's'} will be added with their sessions when the account syncs` : ''}. The originals remain locally.`,
            )
          : tr(`Inloggad som ${name}.`, `Signed in as ${name}.`),
      );
      onProgress?.('reload');
      this.reload(undefined, t.id);
      return result;
    })();
    this.busy = run;
    try {
      return await run;
    } finally {
      this.busy = null;
    }
  }

  /** Användaren avbröt vid valet om gästdata: avsluta den nya sessionen, inget ändras lokalt. */
  async cancelLogin(o: LoginOutcome): Promise<void> {
    await signOut(o.base, o.token).catch(() => undefined);
  }

  // ------------------------------------------------------------------ utloggning

  /**
   * Loggar ut DENNA enhet/session (inte alla användarens enheter) med serverns sessionsåterkallelse.
   * Serverns aktiviteter raderas aldrig. deleteLocal = uttryckligt val att radera den lokala kopian.
   */
  async logout(opts: { deleteLocal: boolean }): Promise<'confirmed' | 'pending'> {
    const p = this.profile;
    if (p.kind !== 'account') return 'confirmed';
    this.emit({ switching: true });
    for (const h of this.beforeSignOut) await h(p).catch(() => undefined);
    const base = this.baseFor(p) ?? '';
    const token = await tokens.getToken(p.id);
    const deviceId = this.store?.device.deviceRegistered ? this.store.device.deviceId : null;
    let confirmed = false;
    // 1) Koppla bort denna enhets push hos servern (kräver sessionen, därför före sign-out).
    if (deviceId) await apiAt({ base, token, expectedUser: p.userId ?? null }, `/api/devices/${encodeURIComponent(deviceId)}/push-subscription`, { method: 'DELETE' }).catch(() => undefined);
    // Native: fjärrpush-tokenen hör till appinstallationen – kontot ska inte längre nå den.
    if (deviceId && isNative()) await apiAt({ base, token, expectedUser: p.userId ?? null }, `/api/devices/${encodeURIComponent(deviceId)}/native-push`, { method: 'DELETE' }).catch(() => undefined);
    // 2) Återkalla sessionen.
    try {
      await signOut(base, token);
      confirmed = true;
    } catch (e) {
      if (!isNetworkError(e)) confirmed = (e as { status?: number }).status === 401; // redan avslutad hos servern
    }
    if (isNative()) {
      if (confirmed) await tokens.clearToken(p.id);
      else await tokens.moveTokenToRevocation(p.id);
    }
    // 3) Lokal kopia: behålls i kontots egen profil (inte som gästdata) – eller raderas efter uttryckligt val.
    if (opts.deleteLocal && this.store) await this.store.storageAdapter.destroy().catch(() => undefined);
    await updateRegistry((r) => {
      const profiles = { ...r.profiles };
      if (opts.deleteLocal) delete profiles[p.id];
      else profiles[p.id] = { ...profiles[p.id]!, signedOut: true };
      const pendingRevocations = confirmed
        ? r.pendingRevocations.filter((x) => x.profileId !== p.id)
        : [...r.pendingRevocations.filter((x) => x.profileId !== p.id), { profileId: p.id, userId: p.userId ?? null, server: base, deviceId, since: nowIso() }];
      return { ...r, activeProfileId: GUEST_ID, profiles, pendingRevocations };
    });
    clearLiveSession();
    setNotice(
      confirmed
        ? opts.deleteLocal
          ? tr('Du är utloggad och den lokala kopian av kontot är raderad på den här enheten. Kontots data på servern finns kvar.', 'You are signed out and the local copy of the account has been deleted on this device. The account’s data on the server is kept.')
          : tr('Du är utloggad på den här enheten.', 'You are signed out on this device.')
        : tr('Utloggad på den här enheten. Servern har ännu inte bekräftat utloggningen.', 'Signed out on this device. The server has not yet confirmed the sign-out.'),
    );
    this.reload('#/idag');
    return confirmed ? 'confirmed' : 'pending';
  }

  /** "Kom ihåg mig" var avstängt och sessionen lever inte längre här: logga ut på enheten. */
  private async endNotRemembered(): Promise<void> {
    const p = this.profile;
    const base = this.baseFor(p) ?? '';
    const token = await tokens.getToken(p.id);
    let confirmed = !token; // webb utan cookie: inget att återkalla från denna enhet
    if (token) {
      try {
        await signOut(base, token);
        confirmed = true;
      } catch (e) {
        if (!isNetworkError(e)) confirmed = true;
      }
      if (confirmed) await tokens.clearToken(p.id);
      else await tokens.moveTokenToRevocation(p.id);
    }
    await updateRegistry((r) => ({
      ...r,
      activeProfileId: GUEST_ID,
      profiles: { ...r.profiles, [p.id]: { ...r.profiles[p.id]!, signedOut: true } },
      pendingRevocations: confirmed ? r.pendingRevocations : [...r.pendingRevocations, { profileId: p.id, userId: p.userId ?? null, server: base, deviceId: null, since: nowIso() }],
    }));
    clearLiveSession();
    setNotice(tr('Du är utloggad eftersom "Kom ihåg mig" var avstängt. Logga in igen för att se och synka kontot. Inga ändringar har raderats.', 'You are signed out because "Remember me" was off. Sign in again to see and sync the account. No changes have been deleted.'));
    this.reload('#/idag');
  }

  /**
   * Återkallar sessioner för utloggningar som gjordes offline – innan någon automatisk återanvändning.
   * Rapporterar "bekräftad" först när servern svarat.
   */
  processPendingRevocations(): Promise<void> {
    if (this.revoking) return this.revoking;
    this.revoking = (async () => {
      const reg = await readRegistry();
      for (const r of reg?.pendingRevocations ?? []) {
        try {
          const token = isNative() ? await tokens.getRevocationToken(r.profileId) : null;
          let kind: 'revoked' | 'nothing' = 'nothing';
          if (!isNative() || token) {
            const user = await getSession(r.server, token);
            if (user && (!r.userId || user.id === r.userId)) {
              if (r.deviceId) {
                await apiAt({ base: r.server, token, expectedUser: user.id }, `/api/devices/${encodeURIComponent(r.deviceId)}/push-subscription`, { method: 'DELETE' }).catch((e) => {
                  if (isNetworkError(e)) throw e;
                });
                if (isNative()) {
                  await apiAt({ base: r.server, token, expectedUser: user.id }, `/api/devices/${encodeURIComponent(r.deviceId)}/native-push`, { method: 'DELETE' }).catch((e) => {
                    if (isNetworkError(e)) throw e;
                  });
                }
              }
              await signOut(r.server, token);
              kind = 'revoked';
            }
          }
          if (isNative()) await tokens.clearRevocationToken(r.profileId);
          const next = await updateRegistry((x) => ({ ...x, pendingRevocations: x.pendingRevocations.filter((y) => y.profileId !== r.profileId) }));
          this.emit({ pendingRevocations: next.pendingRevocations.length });
          this.onRevocationResolved?.(kind);
        } catch (e) {
          if (isNetworkError(e)) break; // försök igen när nätet kommer tillbaka
        }
      }
    })().finally(() => {
      this.revoking = null;
    });
    return this.revoking;
  }

  // ------------------------------------------------------------------ hjälpare

  /** Ladda om sidan med en ny aktiv profil. Andra flikar meddelas och laddar också om. */
  private reload(hash?: string, activeProfileId?: string) {
    this.emit({ switching: true });
    if (activeProfileId) {
      broadcastProfileChange(activeProfileId);
      if (hash) history.replaceState(null, '', hash);
      location.reload();
      return;
    }
    void readRegistry().then((r) => {
      if (r) broadcastProfileChange(r.activeProfileId);
      if (hash) history.replaceState(null, '', hash);
      location.reload();
    }).catch(() => location.reload());
  }

  /** Tidigare verifierad session finns för denna profil (för visning av namn/e-post offline). */
  identity(): { name: string; email: string } | null {
    const p = this.profile;
    if (p.kind !== 'account') return null;
    return { name: p.name?.trim() || p.email?.split('@')[0] || 'Ditt konto', email: p.email ?? '' };
  }
}


let auth: AuthStore | null = null;
export function setAuthStore(a: AuthStore) {
  auth = a;
}
export function getAuthStore(): AuthStore {
  if (!auth) throw new Error('Auth ej initierad');
  return auth;
}
export function useAuth(): AuthSnapshot {
  const a = getAuthStore();
  return useSyncExternalStore(a.subscribe, () => a.snapshot);
}
